import { BeDataTable, BeRow, BeStack, BeSwatch, BeText, type BeTextInk } from "@beos-css/react/index.ts";

/** Contrast is against the 216 panel these sit on — the surface decides whether
 * an ink is readable, so the numbers belong beside the samples.
 *
 * The chip reads the TOKEN, so it cannot drift; only `hex` is transcribed, and
 * secondary's is derived (--be-darken-4 is a color-mix of the 216 panel). */
const INKS: { ink: BeTextInk; token: string; hue: string; hex: string; contrast: string }[] = [
	{ ink: "secondary", token: "--be-card-text", hue: "Grey", hex: "#606060", contrast: "4.41:1" },
	{ ink: "danger", token: "--be-status-danger", hue: "Red", hex: "#d62828", contrast: "3.51:1" },
	{ ink: "info", token: "--be-status-info", hue: "Blue", hex: "#0072ce", contrast: "3.43:1" },
	{ ink: "success", token: "--be-status-success", hue: "Green", hex: "#00a000", contrast: "2.44:1" },
];

export function TextInkDemo() {
	return (
		<section id="demo-text-ink">
			<h2>Text inks (BeText ink=)</h2>
			<p>
				An ink sets <b>two dials, not one</b>. The coverage faces are COLRv0: <code>color</code> paints only the glyph's opaque core, while
				its six antialiasing levels come from the font's CPAL palette and are baked black. So a colour without its matching{" "}
				<code>font-palette</code> renders a hard 1-bit shape wearing a dark fringe, not antialiased text. The prop binds the pair, which is
				what makes it unbreakable — the ramps are generated per ink per family per grid, because <code>override-colors</code> cannot take{" "}
				<code>currentColor</code>.
			</p>
			<BeStack pad="item" className="be-panel">
				<BeDataTable tags>
					<thead>
						<tr>
							<th>ink</th>
							<th>token</th>
							<th>color</th>
							<th>contrast</th>
							<th>sample</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<em>none</em>
							</td>
							<td>inherits the surface</td>
							{/* no chip: the value is whatever surface the run sits on. */}
							<td>—</td>
							<td>—</td>
							<td className="be-data-table__fill">
								<BeText>Hamburgefonstiv 0123</BeText>
							</td>
						</tr>
						{INKS.map((i) => (
							<tr key={i.ink}>
								<td>{i.ink}</td>
								<td>{i.token}</td>
								<td>
									<BeRow gap="hair" align="center">
										<BeSwatch size="mini" color={`var(${i.token})`} title={i.hex} />
										{i.hue}: {i.hex}
									</BeRow>
								</td>
								<td>{i.contrast}</td>
								<td className="be-data-table__fill">
									<BeText ink={i.ink}>Hamburgefonstiv 0123</BeText>
								</td>
							</tr>
						))}
					</tbody>
				</BeDataTable>
			</BeStack>
			<p>
				The names are <code>BeStatusTone</code>'s, so inks and progress-bar segments share one vocabulary rather than growing two.{" "}
				<b>Two tones are deliberately not inks:</b> <code>warning</code> and <code>neutral</code> measure 1.35:1 and 1.67:1 on this panel,
				under even the 3:1 non-text floor. They are fill tones — a segment or a chip, where an adjacent label carries the meaning — so they
				get no ramp and cannot be named here.
			</p>
		</section>
	);
}
